import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import filterActiveStylistsAll from 'bl/data/filterActiveStylistsAll.bl';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import Badge from 'components/generic/Badge.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import CurrencySign from 'components/price/CurrencySign.component';
import TooltipForReceptionist from 'components/TooltipForReceptionist';
import PERM from 'constants/core/perm.const';
import IN from 'constants/in';
import PLAN_NAME from 'constants/plan.const';
import DISPLAY_POLICY_TEXT from 'constants/pos/stripePolicyDisplayedText.const';
import TRANSFER_POLICY_TEXT_LOWER from 'constants/pos/stripeTransferPolicyTextLower.const';
import PRICE_PER_STYLIST from 'constants/settings/ncobStaffPrices.const';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectCurrentSalon, selectCurrentSalonNewPricing, selectCurrentSalonStylists } from 'selectors/currentSalon.selectors';

import ConfirmationModal from 'common/ConfirmationModal';

import CN from './StylistField.module.scss';

const getTransferPolicyText = (transferPolicy) => DISPLAY_POLICY_TEXT[transferPolicy] || 'composite.stripePolicyDisplayedText.unknownPolicy';

const StylistField = ({ stylist, quickSetupLayout, currentSalon, stylistList, isNewPricing, onEditHandler, onDeleteHandler }) => {
  const [deletePopupModal, setDeletePopupModal] = useState(false);
  const [removeOtherModal, setRemoveOtherModal] = useState(false);
  const [essentialOrAdvancedModal, setEssentialOrAdvancedModal] = useState(false);
  const [worningDeletingLastStylistModal, setWorningDeletingLastStylistModal] = useState(false);
  const [pricePerStylist, setPricePerStylist] = useState('');

  const { t } = useTranslation();
  const { isDarkMode } = useTheme();

  const onEdit = useCallback(
    (e) => {
      e.stopPropagation();
      if (stylist.id) {
        onEditHandler(stylist.id);
      }
    },
    [onEditHandler, stylist.id],
  );

  const onDelete = (e) => {
    e.stopPropagation();
    setDeletePopupModal(true);
  };

  const onCancelDelete = () => {
    setDeletePopupModal(false);
  };

  const onConfirmDelete = useCallback(() => {
    const essenPricePerSty = isNewPricing ? PRICE_PER_STYLIST.essentialNewPrice : PRICE_PER_STYLIST.essentialOldPrice;
    const advPricePerSty = isNewPricing ? PRICE_PER_STYLIST.advNewPrice : PRICE_PER_STYLIST.advOldPrice;

    const filteredStylists = filterActiveStylistsAll(stylistList).map((s) => s.user_name);
    const isWaitlist = stylist.serves_as_waitlist;
    const planName = currentSalon?.pricing_plan;

    if (filteredStylists.length === 1 && !isWaitlist) {
      setWorningDeletingLastStylistModal(true);
      setDeletePopupModal(false);
      return;
    }

    if (!isWaitlist) {
      if (planName === PLAN_NAME.essential) {
        setPricePerStylist(essenPricePerSty);
        setEssentialOrAdvancedModal(true);
      } else if (planName === PLAN_NAME.advanced) {
        setPricePerStylist(advPricePerSty);
        setEssentialOrAdvancedModal(true);
      } else {
        setRemoveOtherModal(true);
      }
    } else {
      setRemoveOtherModal(true);
    }

    setDeletePopupModal(false);
  }, [currentSalon?.pricing_plan, isNewPricing, stylist.serves_as_waitlist, stylistList]);

  const onFinalDelete = useCallback(async () => {
    if (!stylist.id) return;

    try {
      await onDeleteHandler(stylist.id);
    } catch (error) {
      console.error('onFinalDelete(), Error deleting stylist', error);
    }
  }, [onDeleteHandler, stylist.id]);

  const goBack = () => {
    setWorningDeletingLastStylistModal(false);
  };

  const {
    serves_as_waitlist: isWaitlist,
    has_remote_login: hasRemoteLogin,
    has_pin_login: hasPinLogin,
    user_name: userName,
    has_column: hasColumn,
    transfer_policy: transferPolicy,
    role,
    email,
    id,
  } = stylist;

  return (
    <div
      data-bem="StylistField"
      data-stylist-id={id}
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <SalonModal tag="RolesScreen__modal-remove-with-subscription" isOpen={essentialOrAdvancedModal} variant="noFooter">
        <SalonModal.Header>
          <div className={CN.closeButtonContainer}>
            <Button variant="empty" onClick={() => setEssentialOrAdvancedModal(false)}>
              <Icon variant="close" variantInvert />
            </Button>
          </div>
        </SalonModal.Header>
        <SalonModal.Body>
          <div className={CN.modalContent}>
            <IconStatus variant="alert" width="lg">
              <Icon variant="alert" width="l1" />
            </IconStatus>
            <div className={CN.modalTitle}>{t('stylistScreen.removeModal.title1')}</div>
            <div className={CN.modalText}>
              {/* TODO: localise price + tax */}
              {t('stylistScreen.removeModal.description1Part1')} <CurrencySign value={pricePerStylist} /> {t('stylistScreen.removeModal.description1Part2')}
            </div>
          </div>
        </SalonModal.Body>
        <SalonModal.Footer>
          <Button variant="alert" xSpace onClick={onFinalDelete}>
            {t('common.buttons.deleteUpp')}
          </Button>
        </SalonModal.Footer>
      </SalonModal>

      <SalonModal tag="RolesScreen__modal-remove-other" isOpen={removeOtherModal} variant="noFooter">
        <SalonModal.Header>
          <div className={CN.closeButtonContainer}>
            <Button variant="empty" onClick={() => setRemoveOtherModal(false)}>
              <Icon variant="close" variantInvert />
            </Button>
          </div>
        </SalonModal.Header>
        <SalonModal.Body>
          <div className={CN.modalContent}>
            <IconStatus variant="alert" width="lg">
              <Icon variant="alert" width="l1" />
            </IconStatus>
            <div className={CN.modalTitle}>{t('stylistScreen.removeModal.title1')}</div>
            <div className={CN.modalText}>{t('stylistScreen.removeModal.description2')}</div>
          </div>
        </SalonModal.Body>
        <SalonModal.Footer>
          <Button variant="alert" xSpace onClick={onFinalDelete}>
            {t('common.buttons.deleteUpp')}
          </Button>
        </SalonModal.Footer>
      </SalonModal>

      <SalonModal tag="RolesScreen__modal-new-role" isOpen={deletePopupModal} withFooter>
        <SalonModal.Header>
          <div className={CN.closeButtonContainer}>
            <Button variant="empty" onClick={onCancelDelete}>
              <Icon variant="close" variantInvert />
            </Button>
          </div>
        </SalonModal.Header>
        <SalonModal.Body>
          <div className={CN.modalContent}>
            <IconStatus variant="alert" width="lg">
              <Icon variant="alert" width="l1" />
            </IconStatus>
            <div className={CN.modalTitle}>{t('stylistScreen.removeModal.title2', { user: userName })}</div>
            <div className={CN.modalText}>{t('stylistScreen.removeModal.description3', { user: userName })}</div>
          </div>
        </SalonModal.Body>
        <SalonModal.Footer>
          <div className={CN.modalButtons}>
            <Button variant="outline" xSpace onClick={onCancelDelete}>
              {t('stylistScreen.removeModal.goBackButton')}
            </Button>
            <Button variant="alert" xSpace onClick={onConfirmDelete}>
              {t('stylistScreen.removeModal.deleteButton')}
            </Button>
          </div>
        </SalonModal.Footer>
      </SalonModal>

      {worningDeletingLastStylistModal && (
        <div className="hide-left-button">
          <ConfirmationModal
            popupTitle={t('stylistScreen.confirmationModalTitle', { user: userName })}
            popupMessage=""
            leftButtonText=""
            rightButtonText={t('common.buttons.close')}
            leftButtonCallback={() => {}}
            rightButtonCallback={goBack}
          />
        </div>
      )}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        onClick={(e) => {
          if (!quickSetupLayout) {
            onEdit(e);
          }
        }}
      >
        <div className={CN.stylistItem}>
          <div className={CN.container}>
            <div
              className={cn({
                [CN.cell]: true,
                [CN.infoColumn]: true,
              })}
            >
              <span className={CN.name}>{userName}</span>
              <span className={CN.role}>{role?.name}</span>
              <div className={CN.email}> {email}</div>
            </div>
            <div
              className={cn({
                [CN.cell]: true,
                [CN.stylistColumn]: true,
              })}
            >
              <div className={CN.mobileLabel}>{t('stylistScreen.bookableColumn')}:</div>
              <div className={CN.badgeContainer}>
                {isWaitlist ? (
                  <Badge variant="fine">
                    <div className={CN.badgeFontNoSpace}>{t('stylistScreen.waitlist')}</div>
                  </Badge>
                ) : (
                  <div className={CN.iconContainer}>
                    <Icon variant={hasColumn ? 'checkCircle' : 'xCircle'} width="xl" />
                  </div>
                )}
              </div>
            </div>
            <div
              className={cn({
                [CN.cell]: true,
                [CN.pinColumn]: true,
              })}
            >
              <div className={CN.mobileLabel}>{t('stylistScreen.pincodeAccess')}:</div>
              <div>
                {!hasPinLogin ? (
                  <Badge variant="alert">
                    <Icon variant="lockOpen" width="xs" />
                    <div className={CN.badgeFont}>{t('common.disabled')}</div>
                  </Badge>
                ) : (
                  <Badge variant="success">
                    <Icon variant="lockClose" width="xs" />
                    <div className={CN.badgeFont}>{t('common.enabled')}</div>
                  </Badge>
                )}
              </div>
            </div>
            <div
              className={cn({
                [CN.cell]: true,
                [CN.emailColumn]: true,
              })}
            >
              <div className={CN.mobileLabel}>{t('stylistScreen.remoteAccess')}:</div>
              <div>
                {!hasRemoteLogin ? (
                  <Badge variant="alert">
                    <Icon variant="cloudOff" width="xs" />
                    <div className={CN.badgeFont}>{t('common.disabled')}</div>
                  </Badge>
                ) : (
                  <Badge variant="success">
                    <Icon variant="cloudOn" width="xs" />
                    <div className={CN.badgeFont}>{t('common.enabled')}</div>
                  </Badge>
                )}
              </div>
            </div>
            <div
              className={cn({
                [CN.cell]: true,
                [CN.stripeColumn]: true,
              })}
            >
              <div className={CN.mobileLabel}>{t('stylistScreen.paymentsAccount')}:</div>
              <div>
                <Badge variant={transferPolicy === TRANSFER_POLICY_TEXT_LOWER.noTransfer ? 'warning' : 'fb'}>
                  <Icon variant="stripeWhite" width="xs" />
                  <div className={CN.badgeFont}>{t(getTransferPolicyText(transferPolicy))}</div>
                </Badge>
              </div>
            </div>
            {!quickSetupLayout && (
              <div className={CN.actionColumn}>
                <Button variant="quaternary" width="auto" onClick={onDelete}>
                  <Icon variant="delete" width="md" />
                  <div className={CN.mobileDeleteLabel}>{t('common.buttons.deleteUpp')}</div>
                </Button>
                <div className={CN.editButtonContainer}>
                  <Button variant="primaryForce" width="medium">
                    <span className={CN.buttonGap}>{t('common.buttons.editUpp')}</span>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <TooltipForReceptionist permission={PERM.canEditStaff} where={IN.deleteStylist} />
    </div>
  );
};

const mapStateToProps = (state) => ({
  currentSalon: selectCurrentSalon(state),
  stylistList: selectCurrentSalonStylists(state),
  isNewPricing: selectCurrentSalonNewPricing(state),
});

const StylistFieldHoc = connect(mapStateToProps)(StylistField);

export default StylistFieldHoc;
